import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type PayrollMode = 'booked' | 'wrench';

export interface PayrollSummaryRow {
  employee_type: 'technician' | 'staff';
  employee_id: string;
  employee_name: string;
  role_label: string;
  clock_minutes: number;
  after_hours_minutes: number;
  produced_minutes: number;
  has_unreviewed_entries: boolean;
  ticket_count: number;
  session_count: number;
  /** oct20e LABOR (FD-2026-10-24-B): distinct quantities — approved labor, actual (session) labor, and the shop's
   *  stored OSH-282 pay basis (null when none recorded). produced_minutes: booked = approved, wrench = actual. */
  approved_labor_minutes?: number;
  actual_labor_minutes?: number;
  pay_basis_minutes?: number | null;
}

interface UsePayrollSummaryParams {
  shopId?: string;
  startDate: Date;
  endDate: Date;
  mode?: PayrollMode;
  includeZeros?: boolean;
}

export const usePayrollSummary = ({
  shopId,
  startDate,
  endDate,
  mode = 'booked',
  includeZeros = false,
}: UsePayrollSummaryParams) => {
  return useQuery({
    queryKey: ['payroll-summary', shopId, startDate.toISOString(), endDate.toISOString(), mode, includeZeros],
    queryFn: async () => {
      if (!shopId) return [];

      const { data, error } = await supabase.rpc('get_payroll_summary', {
        p_shop_id: shopId,
        p_start: startDate.toISOString(),
        p_end: endDate.toISOString(),
        p_mode: mode,
        p_include_zeros: includeZeros,
      });

      if (error) throw error;
      return (data || []) as PayrollSummaryRow[];
    },
    enabled: !!shopId,
  });
};
